/**
 * This method applies default values from a default object when the corresponding values in the
 * input object are undefined.
 *
 * This is especially useful when your type definition isn't directly matched to a network response,
 * but is derived from N sources, and existence of data is not guaranteed.
 *
 * Not using it would lead to unnecessary fallback value usage.
 *
 * @example
 * ```ts
 * type O = { name: string, hasName: boolean };
 * const defaults = { name: '', hasName: false };
 * const values = { name: undefined };
 *
 * // Since 'name' is missing (undefined), the default is applied
 * // Since 'hasName' is missing (undefined), the default is applied
 * withDefaults<O>(defaults, values); // => { name: '', hasName: false }
 * ```
 *
 * @example
 * ```ts
 * type O = { name: string, hasName: boolean };
 * const defaults = { name: '', hasName: false };
 * const values = { name: 'John', hasName: true };
 *
 * // All values exist, so defaults aren't used
 * withDefaults<O>(defaults, values); // => { name: 'John', hasName: true }
 * ```
 *
 * @param defaults - The object containing default values for all required properties
 * @param values - The partial object that may contain some but not all required properties
 * @returns A new object with all properties from defaults, overridden by non-undefined values from values
 */
export const withDefaults = <T>(defaults: T, values: Partial<T>): T => Object.entries(values).reduce((acc, [k, v]) => ({
  ...acc,
  [k]: v === undefined ? acc[k as keyof typeof defaults] : v,
}), { ...defaults });
